import { Send } from "lucide-react";
import { useState } from "react";

import { Badge, type BadgeVariant } from "components/ui/badge";
import { Button } from "components/ui/button";

import type { FeedbackForm } from "../../feedback/schemas";
import { formLabel } from "../announcement-form";
import { formatSendTime } from "../scheduled-announcements";
import {
  canDeleteSent,
  type SentAnnouncement,
  useSentAnnouncements,
} from "../sent-announcements";
import {
  DeleteForEveryoneDialog,
  DeletionActions,
} from "./DeleteForEveryoneDialog";
import { statusChipClass } from "./status-chip";
import { PersonalisedBadge, TemplateText } from "./TemplateText";

const STATUS: Record<
  SentAnnouncement["status"],
  { label: string; variant: BadgeVariant }
> = {
  REQUESTED: { label: "Sending", variant: "warning" },
  SUCCEEDED: { label: "Sent", variant: "success" },
  FAILED: { label: "Failed", variant: "error" },
};

/**
 * What was sent with Send now, newest first, each with "Delete for everyone".
 *
 * Booked announcements are listed with their booking in
 * ScheduledAnnouncementList, which offers the same action on its sent rows.
 */
export function SentAnnouncementList({
  eventId,
  uid,
  timeZone,
  forms = null,
}: {
  eventId: string;
  uid: string;
  timeZone: string | undefined;
  /** The event's feedback forms, to name a linked one; null while loading. */
  forms?: FeedbackForm[] | null;
}) {
  const { items, error, hasMore, loadMore } = useSentAnnouncements(eventId);
  const [deleting, setDeleting] = useState<SentAnnouncement | null>(null);

  if (error) {
    return (
      <p className="text-sm text-destructive-text" data-testid="sent-error">
        Could not load sent announcements: {error}
      </p>
    );
  }
  if (items.length === 0 && !hasMore) return null;

  return (
    <section className="space-y-3" data-testid="sent-announcements">
      <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
        <Send className="h-4 w-4 shrink-0" />
        Sent announcements
      </h3>
      <ul className="space-y-2">
        {items.map((item) => {
          const deleted = item.deletion.state === "deleted";
          return (
            <li
              key={item.id}
              className="rounded-lg border border-border bg-card p-3 text-card-foreground"
              data-testid="sent-announcement"
              data-status={item.status}
              data-deletion={item.deletion.state}
              data-id={item.id}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p
                    className={`break-words font-medium ${deleted ? "text-muted-foreground line-through" : "text-foreground"}`}
                  >
                    <TemplateText text={item.title} />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatSendTime(item.requestedAt, timeZone)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <PersonalisedBadge title={item.title} body={item.body} />
                  <Badge
                    variant={deleted ? "neutral" : STATUS[item.status].variant}
                    className={statusChipClass(
                      deleted ? "neutral" : STATUS[item.status].variant,
                    )}
                    data-testid="sent-status"
                  >
                    {deleted ? "Deleted" : STATUS[item.status].label}
                  </Badge>
                </div>
              </div>
              {item.feedbackFormId && (
                <p
                  className="mt-1 break-words text-sm text-muted-foreground"
                  data-testid="sent-form"
                >
                  Feedback form: {formLabel(forms, item.feedbackFormId)}
                </p>
              )}
              {!deleted &&
                item.status === "SUCCEEDED" &&
                item.recipientCount !== undefined && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sent to {item.recipientCount}{" "}
                    {item.recipientCount === 1 ? "person" : "people"}.
                  </p>
                )}
              {!deleted && item.status === "FAILED" && item.error && (
                <p className="mt-1 break-words text-xs text-destructive-text">
                  {item.error}
                </p>
              )}
              <DeletionActions
                deletion={item.deletion}
                timeZone={timeZone}
                canDelete={canDeleteSent(item)}
                onDelete={() => setDeleting(item)}
              />
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <Button
          variant="outline"
          size="sm"
          data-testid="sent-load-more"
          onClick={loadMore}
        >
          Show older announcements
        </Button>
      )}

      {deleting && (
        <DeleteForEveryoneDialog
          key={deleting.id}
          eventId={eventId}
          uid={uid}
          sendCommandId={deleting.id}
          title={deleting.title}
          onClose={() => setDeleting(null)}
        />
      )}
    </section>
  );
}
